import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Send,
  Bot,
  Settings,
  MessageCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Bell,
  History,
  Zap,
  AlertTriangle,
  Info
} from 'lucide-react';

const TelegramPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Config
  const [config, setConfig] = useState({
    is_configured: false,
    bot_token: '',
    bot_token_masked: '',
    bot_username: '',
    chat_id: '',
    notifications_actives: true,
    types_notifications: ['alertes', 'rappels', 'crm']
  });
  
  // Form
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [showToken, setShowToken] = useState(false);
  
  // Messages
  const [messages, setMessages] = useState([]);
  const [testMessage, setTestMessage] = useState('🌾 Test de connexion Bureau Agricole !');
  
  // Active tab
  const [activeTab, setActiveTab] = useState('config');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, messagesRes] = await Promise.all([
        api.get('/telegram/config').catch(() => ({ data: { is_configured: false } })),
        api.get('/telegram/messages?limit=50').catch(() => ({ data: { messages: [] } }))
      ]);
      
      setConfig(configRes.data);
      setChatId(configRes.data.chat_id || '');
      setMessages(messagesRes.data?.messages || []);
    } catch (error) {
      console.error('Erreur chargement config Telegram:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!botToken && !config.is_configured) {
      toast.error('Veuillez entrer le token du bot');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/telegram/config', {
        bot_token: botToken || undefined,
        chat_id: chatId || null,
        notifications_actives: config.notifications_actives,
        types_notifications: config.types_notifications
      });
      
      if (response.data.success) {
        toast.success(`Bot @${response.data.bot_username} connecté !`);
        setBotToken('');
        loadConfig();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/telegram/test');
      
      if (response.data.success) {
        toast.success(`Connexion OK ! Bot: @${response.data.bot_username}`);
      } else {
        toast.error(response.data.error || 'Erreur de connexion');
      }
    } catch (error) {
      toast.error('Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  const sendTestMessage = async () => {
    if (!chatId) {
      toast.error('Veuillez d\'abord configurer votre Chat ID');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/telegram/send', {
        chat_id: chatId,
        message: testMessage,
        parse_mode: 'HTML'
      });
      
      if (response.data.success) {
        toast.success('Message envoyé !');
        loadConfig(); // Refresh messages
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const sendNotification = async (type, titre, message) => {
    setSending(true);
    try {
      const response = await api.post('/telegram/send-notification', null, {
        params: { type_notif: type, titre, message }
      });
      
      if (response.data.success) {
        toast.success('Notification envoyée !');
      } else {
        toast.error(response.data.reason || 'Erreur d\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const deleteConfig = async () => {
    if (!confirm('Supprimer la configuration Telegram ?')) return;
    
    try {
      await api.delete('/telegram/config');
      toast.success('Configuration supprimée');
      setConfig({ is_configured: false, notifications_actives: false, types_notifications: [] });
      setBotToken('');
      setChatId('');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const toggleNotificationType = (type) => {
    setConfig(prev => {
      const types = prev.types_notifications || [];
      if (types.includes(type)) {
        return { ...prev, types_notifications: types.filter(t => t !== type) };
      } else {
        return { ...prev, types_notifications: [...types, type] };
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="telegram-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-[#0088cc]" />
            Intégration Telegram
          </h1>
          <p className="text-muted-foreground">
            Recevez des notifications et interagissez avec votre système via Telegram
          </p>
        </div>
        <div className="flex items-center gap-2">
          {config.is_configured ? (
            <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Connecté
            </Badge>
          ) : (
            <Badge variant="secondary" className="flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Non configuré
            </Badge>
          )}
          <Button onClick={loadConfig} variant="ghost" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="send" className="gap-2" disabled={!config.is_configured}>
            <Send className="w-4 h-4" />
            Envoyer
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2" disabled={!config.is_configured}>
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2" disabled={!config.is_configured}>
            <History className="w-4 h-4" />
            Historique ({messages.length})
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Guide de configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-[#0088cc]" />
                  Créer votre Bot Telegram
                </CardTitle>
                <CardDescription>
                  Suivez ces étapes pour créer et configurer votre bot
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#0088cc] text-white flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <p className="font-medium">Ouvrez Telegram</p>
                      <p className="text-sm text-muted-foreground">Sur votre téléphone ou ordinateur</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#0088cc] text-white flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <p className="font-medium">Cherchez @BotFather</p>
                      <p className="text-sm text-muted-foreground">C'est le bot officiel de création de bots</p>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-[#0088cc]"
                        onClick={() => window.open('https://t.me/BotFather', '_blank')}
                      >
                        Ouvrir @BotFather <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#0088cc] text-white flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <p className="font-medium">Envoyez /newbot</p>
                      <p className="text-sm text-muted-foreground">Puis suivez les instructions pour nommer votre bot</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#0088cc] text-white flex items-center justify-center text-sm font-bold">4</div>
                    <div>
                      <p className="font-medium">Copiez le Token</p>
                      <p className="text-sm text-muted-foreground">
                        Il ressemble à : <code className="bg-background px-1 rounded">123456789:ABCdef...</code>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#0088cc] text-white flex items-center justify-center text-sm font-bold">5</div>
                    <div>
                      <p className="font-medium">Obtenez votre Chat ID</p>
                      <p className="text-sm text-muted-foreground">
                        Envoyez un message à votre bot, puis allez sur :
                      </p>
                      <code className="text-xs bg-background px-1 rounded block mt-1">
                        https://api.telegram.org/bot[TOKEN]/getUpdates
                      </code>
                      <p className="text-xs text-muted-foreground mt-1">
                        Le Chat ID est dans "chat":{"{"}"id": XXXXXX{"}"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Formulaire de configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Configuration du Bot</CardTitle>
                <CardDescription>
                  {config.is_configured 
                    ? `Bot actuel : @${config.bot_username || 'inconnu'}` 
                    : 'Entrez les informations de votre bot'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Token */}
                <div className="space-y-2">
                  <Label>Token du Bot {config.is_configured && '(laisser vide pour conserver)'}</Label>
                  <div className="relative">
                    <Input
                      type={showToken ? "text" : "password"}
                      value={botToken}
                      onChange={(e) => setBotToken(e.target.value)}
                      placeholder={config.is_configured ? config.bot_token_masked : "123456789:ABCdefGHI..."}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Chat ID */}
                <div className="space-y-2">
                  <Label>Chat ID (pour recevoir les notifications)</Label>
                  <Input
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="123456789"
                  />
                  <p className="text-xs text-muted-foreground">
                    Votre identifiant de conversation Telegram
                  </p>
                </div>

                {/* Notifications actives */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Notifications actives</p>
                    <p className="text-sm text-muted-foreground">Recevoir les alertes automatiques</p>
                  </div>
                  <Switch
                    checked={config.notifications_actives}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, notifications_actives: checked }))}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button onClick={saveConfig} disabled={saving} className="flex-1">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {config.is_configured ? 'Mettre à jour' : 'Connecter'}
                  </Button>
                  
                  {config.is_configured && (
                    <>
                      <Button onClick={testConnection} disabled={testing} variant="outline">
                        {testing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                      </Button>
                      <Button onClick={deleteConfig} variant="outline" className="text-destructive">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>

                {config.is_configured && config.bot_username && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Bot connecté</p>
                        <p className="text-sm text-green-600">@{config.bot_username}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Send Tab */}
        <TabsContent value="send" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Envoyer un message</CardTitle>
              <CardDescription>
                Testez l'envoi de messages via votre bot
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!chatId ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Chat ID non configuré</p>
                    <p className="text-sm text-amber-600">
                      Configurez d'abord votre Chat ID dans l'onglet Configuration pour pouvoir envoyer des messages.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Votre message..."
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground">
                      Vous pouvez utiliser du HTML : &lt;b&gt;gras&lt;/b&gt;, &lt;i&gt;italique&lt;/i&gt;, &lt;code&gt;code&lt;/code&gt;
                    </p>
                  </div>
                  
                  <Button onClick={sendTestMessage} disabled={sending || !testMessage}>
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer le message
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Quick messages */}
          <Card>
            <CardHeader>
              <CardTitle>Messages rapides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setTestMessage('🌾 Test de connexion Bureau Agricole !')}
                >
                  Test connexion
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setTestMessage('📊 <b>Rapport quotidien</b>\n\n• Parcelles: 85 ha\n• Interventions: 3\n• Alertes: 0')}
                >
                  Rapport quotidien
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setTestMessage('⚠️ <b>Alerte Stock</b>\n\nLe produit "Roundup" est en stock faible (2L restants)')}
                >
                  Alerte stock
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setTestMessage('🔔 <b>Rappel</b>\n\nIntervention planifiée demain sur parcelle "Les Graves Nord"')}
                >
                  Rappel
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Types de notifications</CardTitle>
              <CardDescription>
                Choisissez quelles notifications vous souhaitez recevoir
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { id: 'alertes', label: 'Alertes', desc: 'Stocks bas, entretiens à faire, urgences', icon: AlertTriangle, color: 'text-red-600' },
                { id: 'rappels', label: 'Rappels', desc: 'Interventions planifiées, échéances', icon: Bell, color: 'text-amber-600' },
                { id: 'crm', label: 'CRM', desc: 'Nouveaux prospects, relances', icon: MessageCircle, color: 'text-blue-600' },
                { id: 'meteo', label: 'Météo', desc: 'Alertes météo importantes', icon: Info, color: 'text-cyan-600' },
              ].map(notif => (
                <div key={notif.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <notif.icon className={`w-5 h-5 ${notif.color}`} />
                    <div>
                      <p className="font-medium">{notif.label}</p>
                      <p className="text-sm text-muted-foreground">{notif.desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.types_notifications?.includes(notif.id)}
                    onCheckedChange={() => toggleNotificationType(notif.id)}
                  />
                </div>
              ))}
              
              <Button onClick={saveConfig} disabled={saving} className="w-full mt-4">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Sauvegarder les préférences
              </Button>
            </CardContent>
          </Card>

          {/* Test notifications */}
          <Card>
            <CardHeader>
              <CardTitle>Tester les notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button 
                  variant="outline"
                  onClick={() => sendNotification('alerte', 'Alerte Test', 'Ceci est une alerte de test')}
                  disabled={sending || !chatId}
                >
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                  Alerte
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => sendNotification('rappel', 'Rappel Test', 'N\'oubliez pas votre intervention !')}
                  disabled={sending || !chatId}
                >
                  <Bell className="w-4 h-4 mr-2 text-amber-600" />
                  Rappel
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => sendNotification('crm', 'Nouveau prospect', 'EARL des Vignes vient de s\'inscrire')}
                  disabled={sending || !chatId}
                >
                  <MessageCircle className="w-4 h-4 mr-2 text-blue-600" />
                  CRM
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => sendNotification('success', 'Succès', 'Opération terminée avec succès !')}
                  disabled={sending || !chatId}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                  Succès
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Historique des messages</CardTitle>
              <CardDescription>
                Les 50 derniers messages échangés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun message dans l'historique</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg ${
                          msg.direction === 'outgoing' 
                            ? 'bg-[#0088cc]/10 ml-8' 
                            : 'bg-muted mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className="text-xs">
                            {msg.direction === 'outgoing' ? '→ Envoyé' : '← Reçu'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.created_at).toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        {msg.from_username && (
                          <p className="text-xs text-muted-foreground mt-1">
                            De: @{msg.from_username}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TelegramPage;
